import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Friend {
  id: string;
  firstName: string;
  lastName: string;
  email?: string; // Optional - only present for SUPERADMIN
  avatarUrl?: string;
  loyaltyNumber: string;
  friendshipId: string;
  friendsSince: string;
}

interface FriendRequest {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string; // Optional - only present for SUPERADMIN
    avatarUrl?: string;
  };
  createdAt: string;
}

const FriendsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [requests, setRequests] = useState<FriendRequest[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'friends' | 'requests' | 'search'>('friends');
  const [loading, setLoading] = useState(true);

  // Check if user is super admin
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  useEffect(() => {
    fetchFriends();
    fetchRequests();
  }, []);

  const fetchFriends = async () => {
    try {
      const response = await api.get('/friends');
      setFriends(response.data.friends || []);
    } catch (error) {
      console.error('Error fetching friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await api.get('/friends/requests');
      // API returns { sent: [...], received: [...] }
      // We only show received requests (pending requests sent to the user)
      const receivedRequests = response.data.received || [];
      setRequests(receivedRequests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load friend requests');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const response = await api.get(`/users/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.users || []);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleSendRequest = async (userId: string) => {
    try {
      await api.post('/friends/request', { targetUserId: userId });
      toast.success('Friend request sent');
      handleSearch(); // Refresh search results
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send request');
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    try {
      await api.put(`/friends/accept/${requestId}`);
      toast.success('Friend request accepted');
      fetchFriends();
      fetchRequests();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      await api.delete(`/friends/reject/${requestId}`);
      toast.success('Friend request rejected');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleRemoveFriend = async (friendshipId: string) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) return;
    try {
      await api.delete(`/friends/${friendshipId}`);
      toast.success('Friend removed');
      fetchFriends();
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('friends.title')}</h1>

      <div className="flex space-x-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('friends')}
          className={`pb-2 px-4 ${
            activeTab === 'friends'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-500'
          }`}
        >
          {t('friends.friendsList')}
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`pb-2 px-4 ${
            activeTab === 'requests'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-500'
          }`}
        >
          {t('friends.friendRequests')} {requests.length > 0 && `(${requests.length})`}
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`pb-2 px-4 ${
            activeTab === 'search'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-500'
          }`}
        >
          {t('friends.searchUsers')}
        </button>
      </div>

      {activeTab === 'friends' && (
        <div className="space-y-4">
          {friends.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('friends.noFriends')}</p>
          ) : (
            friends.map((friend) => (
              <div
                key={friend.friendshipId}
                className="bg-white rounded-lg shadow p-4 flex items-center justify-between"
              >
                <Link
                  to={`/friends/${friend.id}`}
                  className="flex-1 flex items-center space-x-3 hover:opacity-80"
                >
                  {friend.avatarUrl ? (
                    <img
                      src={friend.avatarUrl}
                      alt={`${friend.firstName} ${friend.lastName}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-medium">
                        {friend.firstName?.[0]}{friend.lastName?.[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {friend.firstName} {friend.lastName}
                    </h3>
                    {isSuperAdmin && friend.email && (
                      <p className="text-sm text-gray-500">{friend.email}</p>
                    )}
                  </div>
                </Link>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleRemoveFriend(friend.friendshipId);
                  }}
                  className="ml-4 text-red-600 hover:text-red-700 text-sm"
                >
                  {t('friends.remove')}
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="space-y-4">
          {requests.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('friends.noRequests')}</p>
          ) : (
            requests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-lg shadow p-4 flex items-center justify-between"
              >
                <div className="flex items-center space-x-3">
                  {request.user.avatarUrl ? (
                    <img
                      src={request.user.avatarUrl}
                      alt={`${request.user.firstName} ${request.user.lastName}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-medium">
                        {request.user.firstName?.[0]}{request.user.lastName?.[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {request.user.firstName} {request.user.lastName}
                    </h3>
                    {isSuperAdmin && request.user.email && (
                      <p className="text-sm text-gray-500">{request.user.email}</p>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleAcceptRequest(request.id)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                  >
                    {t('friends.accept')}
                  </button>
                  <button
                    onClick={() => handleRejectRequest(request.id)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
                  >
                    {t('friends.reject')}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'search' && (
        <div>
          <div className="flex space-x-2 mb-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('friends.searchPlaceholder')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {t('common.search')}
            </button>
          </div>
          <div className="space-y-4">
            {searchQuery && searchResults.length === 0 && (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500 text-lg">No users found</p>
                <p className="text-gray-400 text-sm mt-2">Try searching with a different name</p>
              </div>
            )}
            {searchResults.map((user) => (
              <div
                key={user.id}
                className="bg-white rounded-lg shadow p-4 flex items-center justify-between"
              >
                <div>
                  <h3 className="font-medium">
                    {user.firstName} {user.lastName}
                  </h3>
                  {isSuperAdmin && user.email && (
                    <p className="text-sm text-gray-500">{user.email}</p>
                  )}
                </div>
                <button
                  onClick={() => handleSendRequest(user.id)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                >
                  {t('friends.sendRequest')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FriendsPage;
